<template>
  <div class="dlist">
    <div
      v-for="contact in contacts"
      :key="contact.id"
      class="dcard"
      @click="$refs.detail.open(contact)"
      >
      <span class="dcard__avatar">{{ initial(contact) }}</span>

      <div class="dcard__main">
        <div class="dcard__name">{{ contact.profile.name || 'Sin nombre' }}</div>
        <div v-if="contact.profile.email || contact.profile.phone" class="dcard__rows">
          <span v-if="contact.profile.email" class="dcard__row">
            <hero-icon :icon="EnvelopeIcon" :size="13" /><span>{{ contact.profile.email }}</span>
          </span>
          <span v-if="contact.profile.phone" class="dcard__row">
            <hero-icon :icon="PhoneIcon" :size="13" /><span>{{ contact.profile.phone }}</span>
          </span>
        </div>
      </div>

      <div v-if="contact.profile.address" class="dcard__addr">
        <hero-icon :icon="MapPinIcon" :size="13" /><span>{{ contact.profile.address }}</span>
      </div>
    </div>

    <div v-if="loading" class="dlist__loading">Cargando…</div>
    <div v-else-if="!contacts.length" class="dlist__empty">
      {{ $t('contact.list.no_contacts_found') }}.
    </div>
    <div ref="sentinel" class="dlist__sentinel"></div>

    <contact-detail ref="detail" @reload="reload"></contact-detail>
  </div>
</template>

<script>
import { Contacts } from '@/graphql/queries/directory'
import HeroIcon from '@/components/shared/HeroIcon'
import ContactDetail from '@/components/directories/Detail'
import EnvelopeIcon from 'heroicons/24/outline/envelope.svg'
import PhoneIcon from 'heroicons/24/outline/phone.svg'
import MapPinIcon from 'heroicons/24/outline/map-pin.svg'

export default {
  name: "DirectoryList",

  components: { HeroIcon, ContactDetail },

  data () {
    return {
      EnvelopeIcon,
      PhoneIcon,
      MapPinIcon,
      contacts: [],
      page: 1,
      pages: 1,
      perPage: 20,
      loading: false,
      lastSearch: "",
      lastFilters: {},
      observer: null
    }
  },

  mounted () {
    this.fetchContacts('cache-first')

    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) this.loadMore()
      },
      { rootMargin: '400px' }
    )
    if (this.$refs.sentinel) this.observer.observe(this.$refs.sentinel)
  },

  beforeDestroy () {
    if (this.observer) this.observer.disconnect()
  },

  methods: {
    initial (contact) {
      const name = contact.profile && contact.profile.name && contact.profile.name.trim()
      return name ? name.charAt(0).toUpperCase() : '?'
    },

    // called by the parent (Directory) on search/role change -> reset to page 1
    fetchContacts (policy = 'cache-first', search = this.lastSearch, filters = this.lastFilters) {
      this.lastSearch = search
      this.lastFilters = filters
      this.page = 1
      this.fetchPage(policy, false)
    },

    fetchPage (policy, append) {
      this.loading = true

      const roles = this.lastFilters.roles
      const rest = Object.assign({}, this.lastFilters)
      delete rest.roles

      this.$apollo.query({
        query: Contacts,
        variables: {
          search: this.lastSearch,
          roles: roles && roles.length ? roles : null,
          filters: rest,
          page: this.page,
          perPage: this.perPage
        },
        fetchPolicy: policy
      }).then(res => {
        const list = res.data.contacts.contacts
        this.contacts = append ? this.contacts.concat(list) : list
        this.pages = res.data.contacts.pages
      }).finally(() => {
        this.loading = false
        this.$nextTick(this.maybeLoadMore)
      })
    },

    loadMore () {
      if (this.loading || this.page >= this.pages) return
      this.page += 1
      this.fetchPage('no-cache', true)
    },

    maybeLoadMore () {
      const el = this.$refs.sentinel
      if (el && el.getBoundingClientRect().top <= window.innerHeight + 400) this.loadMore()
    },

    reload () {
      this.fetchContacts('no-cache')
    }
  }
}
</script>

<style scoped>
.dlist { display: flex; flex-direction: column; gap: 10px; }

.dcard {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.dcard:hover {
  border-color: #dcd4c6;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}

.dcard__avatar {
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #1a1814;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
}

.dcard__main { flex: 1; min-width: 0; }
.dcard__name {
  font-size: 14.5px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dcard__rows { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 3px; }
.dcard__row {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  font-size: 12.5px;
  color: #756f64;
}
.dcard__row span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dcard__row .hero-icon { color: #a39a8c; flex: none; }

.dcard__addr {
  flex: none;
  width: 36%;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #756f64;
}
.dcard__addr span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dcard__addr .hero-icon { color: #a39a8c; flex: none; }

.dlist__loading { text-align: center; color: #a39a8c; font-size: 13px; padding: 14px 0 4px; }
.dlist__empty {
  border: 1px dashed #dcd4c6;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #a39a8c;
  font-size: 14px;
}
.dlist__sentinel { height: 1px; }

@media (max-width: 700px) {
  .dcard { flex-wrap: wrap; }
  .dcard__addr {
    width: 100%;
    padding-left: 58px;
    border-top: 1px solid #f1ece3;
    padding-top: 8px;
  }
}
</style>
